import { type Theme, useTheme } from '@mui/material/styles';
import {
  DataGrid,
  type GridCellParams,
  type GridColDef,
  type GridPaginationModel,
  type GridSortModel
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router';
import { type SortDir } from '../../stores/common/pagination-query-params';
import NoRowsOverlay from './NoRowsOverlay';

const getStyles = (theme: Theme) => ({
  dataGridContainer: {
    height: 500,
    width: '100%'
  },
  dataGrid: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.background.paper,
    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell, .MuiDataGrid-iconSeparator': {
      borderRight: `1px solid ${theme.palette.primary.main}`
    },
    '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
      borderBottom: `1px solid ${theme.palette.primary.main}`
    },
    '& .inactive-row': {
      bgcolor: (theme: Theme) => theme.palette.error.main
    }
  }
});

interface RecordsListProps {
  columns: GridColDef<any>[];
  rows: any[];
  page?: number;
  pageSize: number;
  rowCount: number;
  loading?: boolean;
  canEdit: boolean;
  editUrl: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: SortItem) => void;
}

export interface SortItem {
  field: string;
  sort: SortDir;
}

const RecordsList = ({
  columns,
  rows,
  page,
  pageSize,
  rowCount,
  loading,
  canEdit,
  editUrl,
  onPageChange,
  onSortChange
}: RecordsListProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = getStyles(theme);

  const handleCellClick = (params: GridCellParams) => {
    if (!canEdit) return;
    navigate(`${editUrl}/${params.row.id}`);
  };

  const handlePageChange = (model: GridPaginationModel) => {
    onPageChange(model.page);
  };

  const handleSortChange = (data: GridSortModel) => {
    const [x] = data;
    onSortChange(x);
  };

  return (
    <div style={classes.dataGridContainer}>
      <DataGrid
        sx={classes.dataGrid}
        columns={columns}
        rows={rows}
        loading={loading}
        disableColumnResize={true}
        slots={{
          noRowsOverlay: NoRowsOverlay
        }}
        getRowClassName={(params) =>
          params.row.activo === false || params.row.activa === false ? 'inactive-row' : ''
        }
        onCellClick={handleCellClick}
        onPaginationModelChange={handlePageChange}
        onSortModelChange={handleSortChange}
        paginationModel={{
          page: page ?? 0,
          pageSize
        }}
        rowCount={rowCount}
        pageSizeOptions={[10]}
        paginationMode="server"
        autoHeight={false}
        pagination
        disableColumnMenu
        disableRowSelectionOnClick
      />
    </div>
  );
};

export default RecordsList;
